import { API_BASE_URL } from '@/config/api-url';

/**
 * WEB-523 (Variant A): optional crash detail forwarded to the backend
 * beacon. All fields are best-effort and may be omitted — see backend
 * ClientErrorReportDto (message ≤2000, stack ≤8000, url ≤2000). The backend
 * scrubs message/stack/url (→ route) for PII/secrets before persisting
 * (AdminErrorCaptureService.captureError) — this module does NOT scrub;
 * it only forwards what the caller passed.
 */
export interface ReportClientCrashInput {
  readonly message?: string;
  readonly stack?: string;
  readonly url?: string;
}

/**
 * WEB-464 #2 / WEB-523: best-effort beacon so a FE crash is counted AND
 * detailed server-side (CrashCounterService.increment('fe') +
 * AdminErrorEventRepository row via POST /health/client-error) even when
 * Sentry is not configured (VITE_SENTRY_DSN unset — Sentry.ErrorBoundary is
 * then a no-op wrapper that still invokes `onError`).
 *
 * Called with no argument (or with all fields omitted) it still sends an
 * empty JSON body — the counter-only contract from WEB-464 keeps working.
 * Mirrors utils/analytics/trackEvent.ts's fetch/keepalive/swallow pattern.
 */
export function reportClientCrash(input?: ReportClientCrashInput): void {
  try {
    fetch(`${API_BASE_URL}/health/client-error`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // JSON.stringify drops keys whose value is undefined, so omitted
      // fields never appear in the body (matches the DTO's optional fields).
      body: JSON.stringify({
        message: input?.message,
        stack: input?.stack,
        url: input?.url,
      }),
      keepalive: true,
    }).catch(() => {
      // best-effort — a failed crash report must never surface to the user
    });
  } catch {
    // ignore — fetch may be unavailable in some environments
  }
}
